import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "../../store";
import { DispatchLogger } from "../../utils/dispatch-logger/DispatchLogger";
import {
  LookupsResponse,
  LookupsViewModel,
} from "../../../api/generated/models";
import { ELogTypes } from "../../../shared/enums/logging.enums";
import { IStandardError } from "../../../shared/types/error.types";
import ErrorConstants from "../../../api/constants/error.constants";
import { ApiService } from "../../../api/api.service";
import { setLoading } from "../global/global.slice";

export interface ILookupsState {
  lookupCollection: LookupsViewModel | null;
  isLoading: boolean;
  lookupCollectionError: any;
}

/**
 * --------------------------------------------------------------------------
 *  INITIAL STATE
 * --------------------------------------------------------------------------
 */
const initialState: ILookupsState = {
  lookupCollection: null,
  isLoading: false,
  lookupCollectionError: null,
};

/**
 * --------------------------------------------------------------------------
 *  STATE SLICE
 * --------------------------------------------------------------------------
 */
const lookupsSlice = createSlice({
  name: "lookups",
  initialState,
  reducers: {
    getLookupsSuccess(state, action: PayloadAction<LookupsResponse>) {
      state.lookupCollection = action.payload;
      state.isLoading = false;
      state.lookupCollectionError = null;
    },
    getLookupsFailed(state, action: PayloadAction<LookupsResponse>) {
      const error = { ...action.payload };
      state.lookupCollection = null;
      state.isLoading = false;
      state.lookupCollectionError = error;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    clearLookupsError(state) {
      state.lookupCollectionError = null;
    },
  },
});

/**
 * --------------------------------------------------------------------------
 *  ACTIONS
 * --------------------------------------------------------------------------
 */
export const {
  getLookupsSuccess,
  getLookupsFailed,
  setIsLoading,
  clearLookupsError,
} = lookupsSlice.actions;

/**
 * --------------------------------------------------------------------------
 *  THUNKS
 * --------------------------------------------------------------------------
 */
export const fetchLookups = (): any => async (dispatch: AppDispatch) => {
  dispatch(setIsLoading(true));
  const apiService: ApiService = ApiService.getInstance();
  const { lookupsApi } = apiService;
  try {
    const results = (await lookupsApi.getLookups()) as LookupsResponse;
    const dispatchLogger = DispatchLogger.getInstance(dispatch);
    if (results.isAxiosError || results.name === "Error") {
      const axiosError = results.toJSON();
      const error: IStandardError = {
        message: axiosError.message,
        code: axiosError.code,
        displayMessage: ErrorConstants.GENERIC_ERROR_MESSAGE,
      };
      dispatchLogger.fire(
        getLookupsFailed,
        { ...error },
        {
          message: "Lookup Collection Fetch Failed",
          type: ELogTypes.ERROR,
          data: { error },
        }
      );
      dispatch(setLoading(false));
    } else {
      dispatchLogger.fire(
        getLookupsSuccess,
        { ...results },
        {
          message: "Lookup Collection Fetched",
          type: ELogTypes.API,
          data: { results },
        }
      );
    }
    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(getLookupsFailed(error));
    dispatch(setLoading(false));
  }
};

export default lookupsSlice.reducer;
